// src/lib/speech/speak.ts
type SpeakProfile = "manager" | "customer";
type SpeakProvider = "system" | "custom";

import { getVoiceEnabled, getVoiceProvider, getVoiceVolume } from "./voiceSettings";

export type SpeakOptions = {
  rate?: number;
  pitch?: number;
  volume?: number;

  profile?: SpeakProfile;      // future: kiosk customer voice
  interrupt?: boolean;         // default true
  provider?: SpeakProvider;    // override
  voice?: string;              // OpenAI voice (e.g., "marin")
};

function isEnabled(): boolean {
  return getVoiceEnabled();
}

function getPreferredProvider(): SpeakProvider {
  return getVoiceProvider();
}

function speakSystem(text: string, opts?: SpeakOptions) {
  if (typeof window === "undefined" || !("speechSynthesis" in window)) return;
  if (!text?.trim()) return;

  if (opts?.interrupt !== false) window.speechSynthesis.cancel();

  const utterance = new SpeechSynthesisUtterance(text);
  if (opts?.rate) utterance.rate = opts.rate;
  if (opts?.pitch) utterance.pitch = opts.pitch;
  // Per dev-sync note f7e2c436 the lead-finalize coach was too loud — every
  // speak() call now honors the user's saved volume preference unless the
  // caller passes an explicit `opts.volume`. Range is [0, 1].
  utterance.volume = opts?.volume !== undefined ? opts.volume : getVoiceVolume();

  window.speechSynthesis.speak(utterance);
}

// --- Custom TTS playback state (module-level) ---
let currentAudio: HTMLAudioElement | null = null;
let currentAbort: AbortController | null = null;

// Small in-memory cache to reduce repeat latency (optional but useful)
const audioCache = new Map<string, Blob>();
const cacheKey = (text: string, voice: string, profile: string) =>
  `${voice}|${profile}|${text}`;

async function speakCustom(text: string, opts?: SpeakOptions): Promise<void> {
  const profile = opts?.profile ?? "manager";
  const voice = opts?.voice ?? "marin"; // recommended voice per OpenAI docs :contentReference[oaicite:6]{index=6}

  if (opts?.interrupt !== false) {
    try { currentAbort?.abort(); } catch { /* intentional */ }
    currentAbort = null;
    try { currentAudio?.pause(); } catch { /* intentional */ }
    currentAudio = null;
  }

  const key = cacheKey(text, voice, profile);
  const cached = audioCache.get(key);

  let blob: Blob;

  if (cached) {
    blob = cached;
  } else {
    const ac = new AbortController();
    currentAbort = ac;

    const res = await fetch("/api/tts", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      signal: ac.signal,
      body: JSON.stringify({
        text,
        profile,
        voice,
        format: "mp3",
      }),
    });

    if (!res.ok) {
      const t = await res.text().catch(() => "");
      throw new Error(`TTS failed (${res.status}): ${t.slice(0, 200)}`);
    }

    blob = await res.blob();
    // Cache modestly (keep memory in check)
    if (audioCache.size > 80) audioCache.clear();
    audioCache.set(key, blob);
  }

  const url = URL.createObjectURL(blob);

  const audio = new Audio(url);
  // Apply the user volume preference to the custom-TTS audio element too.
  // Same dev-sync note f7e2c436 — without this the OpenAI voice would play
  // back at 100% even when system TTS respected the slider.
  audio.volume = opts?.volume !== undefined ? Math.max(0, Math.min(1, opts.volume)) : getVoiceVolume();
  currentAudio = audio;

  await new Promise<void>((resolve, reject) => {
    audio.onended = () => resolve();
    audio.onerror = () => reject(new Error("Audio playback error"));
    audio.play().catch(reject);
  });

  URL.revokeObjectURL(url);
}

export function speak(text: string, opts?: SpeakOptions) {
  if (!text?.trim()) return;
  if (typeof window === "undefined") return;
  if (!isEnabled()) return;

  const provider = opts?.provider ?? getPreferredProvider();

  if (provider === "custom") {
    void speakCustom(text, opts).catch(() => {
      // Fallback to system voice if custom fails or offline
      speakSystem(text, opts);
    });
    return;
  }

  speakSystem(text, opts);
}